import React from 'react';

import {
  Button,
  Divider,
  Group,
  LoadingOverlay,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  Alert,
  Anchor,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import { IconInfoCircle } from '@tabler/icons-react';
import Drawer from 'components/Drawer';
import ExpandableSection from 'components/ExpandableSection';
import KiloSelect from 'components/KiloSelect';
import usePackage from 'hooks/package/usePackage';
import useUpdateSalesPortalOptions from 'hooks/package/useUpdateSalesPortalOptions';
import { BillingPlan, PackageRetrieve, PackageUpdateSalesPortalOptions } from 'types';
import notificationHandler from 'utils/notifications';

import FormFieldContent from './components/FormFieldContent';

const formId = 'sales-portal-options-form';

interface ISalesPortalOptionsDrawerProps {
  isOpen: boolean;
  closeDrawer: () => void;
  packageId: string;
  permission?: boolean;
}

export interface SalesPortalOptionsFormValues {
  billingPlans: {
    billingPlanId: string;
    name: string;
    salesPortalStatus: BillingPlan['salesPortalStatus'];
    athleteSalesPortalStatus: BillingPlan['athleteSalesPortalStatus'];
    salesPortalRequiresPaymentMethod: string;
    salesPortalUrl: string;
  }[];
  name: string;
  salesPortalUrl: string;
  salesPortalStatus: PackageRetrieve['salesPortalStatus'];
  athleteSalesPortalStatus: PackageRetrieve['athleteSalesPortalStatus'];
  dobVisible: boolean;
  dobRequired: boolean;
  genderVisible: boolean;
  genderRequired: boolean;
  addressVisible: boolean;
  addressRequired: boolean;
  emergencyNameVisible: boolean;
  emergencyNameRequired: boolean;
  emergencyPhoneVisible: boolean;
  emergencyPhoneRequired: boolean;
  notesVisible: boolean;
  notesRequired: boolean;
}

const SalesPortalOptionsDrawer = ({
  isOpen,
  closeDrawer,
  packageId,
  permission = true,
}: ISalesPortalOptionsDrawerProps): JSX.Element => {
  const { data, isSuccess, isLoading } = usePackage(packageId);
  const { successNotification, errorNotification } = notificationHandler();
  const [billingPlansOpened, { toggle: handleBillingPlansToggle }] = useDisclosure();
  const [formsOpened, { toggle: handleFormsToggle }] = useDisclosure();

  const updateMutation = useUpdateSalesPortalOptions();

  const form = useForm<SalesPortalOptionsFormValues>({
    initialValues: {
      billingPlans: [],
      salesPortalStatus: 'DISABLED',
      athleteSalesPortalStatus: 'DISABLED',
      salesPortalUrl: '',
      dobVisible: false,
      dobRequired: false,
      genderVisible: false,
      genderRequired: false,
      addressVisible: false,
      addressRequired: false,
      emergencyNameVisible: false,
      emergencyNameRequired: false,
      emergencyPhoneVisible: false,
      emergencyPhoneRequired: false,
      notesVisible: false,
      notesRequired: false,
      name: '',
    },
  });

  const handleCloseAndReset = (): void => {
    form.reset();
    closeDrawer();
  };

  const handleSubmit = (values: SalesPortalOptionsFormValues): void => {
    if (data?.data) {
      const payload: PackageUpdateSalesPortalOptions = {
        ...values,
        billingPlans: values.billingPlans.map(billingPlan => ({
          ...billingPlan,
          salesPortalStatus:
            values.salesPortalStatus === 'DISABLED' ? 'DISABLED' : billingPlan.salesPortalStatus,
          athleteSalesPortalStatus:
            values.athleteSalesPortalStatus === 'DISABLED'
              ? 'DISABLED'
              : billingPlan.athleteSalesPortalStatus,
          salesPortalRequiresPaymentMethod: billingPlan.salesPortalRequiresPaymentMethod == 'Yes',
        })),
        packageSalesPortalConfiguration: {
          dobVisible: values.dobVisible,
          dobRequired: values.dobRequired,
          genderVisible: values.genderVisible,
          genderRequired: values.genderRequired,
          addressVisible: values.addressVisible,
          addressRequired: values.addressRequired,
          emergencyNameVisible: values.emergencyNameVisible,
          emergencyNameRequired: values.emergencyNameRequired,
          emergencyPhoneVisible: values.emergencyPhoneVisible,
          emergencyPhoneRequired: values.emergencyPhoneRequired,
          notesVisible: values.notesVisible,
          notesRequired: values.notesRequired,
        },
        packageId,
      };

      updateMutation.mutate(payload, {
        onError: error => {
          errorNotification('Sales portal configuration update failed', error.response?.data.message);
        },
        onSuccess: () => {
          successNotification('Sales portal configuration update success');
          handleCloseAndReset();
        },
      });
    }
  };

  const handleSelect = (value: PackageRetrieve['salesPortalStatus']): void => {
    form.setFieldValue('salesPortalStatus', value);
  };

  const handleSelectAthleteAppVisibility = (value: PackageRetrieve['athleteSalesPortalStatus']): void => {
    form.setFieldValue('athleteSalesPortalStatus', value);
  };

  const isAthleteSalesPortalEnabled = form.values.athleteSalesPortalStatus === 'ENABLED';

  const isPackageSalesPortalEnabled = form.values.salesPortalStatus !== 'DISABLED';

  const isGymSalesPortalEnabled = !!data?.data.salesPortalEnabled;
  const isSalesPortalDisclaimerVisible =
    !isGymSalesPortalEnabled &&
    (form.values.athleteSalesPortalStatus !== 'DISABLED' || form.values.salesPortalStatus !== 'DISABLED');

  useShallowEffect(() => {
    if (data?.data && isSuccess) {
      form.setValues({
        billingPlans:
          data.data.billingPlans?.map(billingPlan => ({
            ...billingPlan,
            salesPortalUrl: `${data.data.salesPortalUrl}/${billingPlan.billingPlanId}`,
            salesPortalRequiresPaymentMethod: billingPlan.salesPortalRequiresPaymentMethod ? 'Yes' : 'No',
          })) || [],
        salesPortalStatus: data.data.salesPortalStatus,
        athleteSalesPortalStatus: data.data.athleteSalesPortalStatus,
        salesPortalUrl: data.data.salesPortalUrl,
        dobVisible: data.data.packagesalesportalconfiguration.dobVisible,
        dobRequired: data.data.packagesalesportalconfiguration.dobRequired,
        genderVisible: data.data.packagesalesportalconfiguration.genderVisible,
        genderRequired: data.data.packagesalesportalconfiguration.genderRequired,
        addressVisible: data.data.packagesalesportalconfiguration.addressVisible,
        addressRequired: data.data.packagesalesportalconfiguration.addressRequired,
        emergencyNameVisible: data.data.packagesalesportalconfiguration.emergencyNameVisible,
        emergencyNameRequired: data.data.packagesalesportalconfiguration.emergencyNameRequired,
        emergencyPhoneVisible: data.data.packagesalesportalconfiguration.emergencyPhoneVisible,
        emergencyPhoneRequired: data.data.packagesalesportalconfiguration.emergencyPhoneRequired,
        notesVisible: data.data.packagesalesportalconfiguration.notesVisible,
        notesRequired: data.data.packagesalesportalconfiguration.notesRequired,
        name: data.data.name,
      });
    }
  }, [data?.data]);

  return (
    <Drawer
      opened={isOpen}
      size='xl'
      onClose={handleCloseAndReset}
      title={<Title order={3}>{form.values.name}</Title>}
      footer={
        <Group position='right'>
          <Button
            variant='subtle'
            onClick={handleCloseAndReset}
            disabled={updateMutation.isLoading || isLoading}
          >
            Cancel
          </Button>
          <Button
            form={formId}
            type='submit'
            loading={updateMutation.isLoading}
            disabled={!permission || isLoading}
            data-cy='saveSalesPortalOptions'
          >
            Save
          </Button>
        </Group>
      }
    >
      <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
        <LoadingOverlay visible={isLoading} />
        <Stack spacing='lg'>
          <Stack spacing={0}>
            {isSalesPortalDisclaimerVisible && (
              <Alert icon={<IconInfoCircle />} mb='xl' color='yellow' title='Sales Portal Disabled'>
                Your Sales Portal is currently disabled. Users won’t be able to access this package until the
                Sales Portal is enabled.
                <br />
                <Anchor
                  href='/products/sales-portal'
                  color='kiloBlue'
                  underline={true}
                  sx={{ textDecoration: 'underline' }}
                >
                  Enable Sales Portal
                </Anchor>
              </Alert>
            )}

            <Text size='md' weight={'bold'}>
              Sales Portal Visibility
            </Text>
            <Text size='sm' color='dimmed' mb='.5rem'>
              Enable this package in the sales portal and configure settings.
            </Text>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'nowrap',
                gap: '1rem',
                width: '100%',
              }}
            >
              <KiloSelect
                rootVariantType='short'
                placeholder='Select visibility'
                data={['PUBLIC', 'PRIVATE', 'DISABLED']}
                onChange={handleSelect}
                value={form.values.salesPortalStatus}
              />
              {isPackageSalesPortalEnabled && (
                <>
                  <TextInput
                    size='sm'
                    style={{ flexGrow: 1 }}
                    disabled={true}
                    value={form.values.salesPortalUrl}
                  />
                  <Button
                    size='sm'
                    disabled={!isPackageSalesPortalEnabled || !permission}
                    onClick={() => {
                      if (!!form.values.salesPortalUrl) {
                        navigator.clipboard.writeText(form.values.salesPortalUrl);
                        successNotification('Copied URL to clipboard');
                      }
                    }}
                  >
                    Copy URL
                  </Button>
                </>
              )}
            </div>
          </Stack>

          <Stack spacing={0}>
            <Text size='md' weight={'bold'}>
              Athlete App Visibility
            </Text>
            <Text size='sm' color='dimmed' mb='0.5rem'>
              Enable this package in the sales portal and configure settings.
            </Text>
            <KiloSelect
              rootVariantType='short'
              placeholder='Select visibility'
              data={['ENABLED', 'DISABLED']}
              onChange={handleSelectAthleteAppVisibility}
              value={form.values.athleteSalesPortalStatus}
            />
          </Stack>

          {(isAthleteSalesPortalEnabled || isPackageSalesPortalEnabled) && (
            <>
              <Divider mx='-xl' />
              <ExpandableSection
                title='Billing Plans'
                opened={billingPlansOpened}
                handleToggle={handleBillingPlansToggle}
                dataCy='billingPlansDropdown'
              >
                <Stack pl='xl' spacing='lg'>
                  {form.values.billingPlans.map((_, i) => {
                    return (
                      <Stack spacing='sm' key={i}>
                        <Text fw={'bold'} mb='-0.25rem'>
                          {form.values.billingPlans[i].name}
                        </Text>
                        {isPackageSalesPortalEnabled && (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'end',
                              flexWrap: 'nowrap',
                              gap: '1rem',
                              width: '100%',
                            }}
                          >
                            <Select
                              label='Sales Portal Visibility'
                              data-cy='billingPlanSalesPortalStatusSelect'
                              size='sm'
                              disabled={!permission}
                              data={['DISABLED', 'PUBLIC', 'PRIVATE']}
                              key={`salesPortalStatus${i}`}
                              {...form.getInputProps(`billingPlans.${i}.salesPortalStatus`)}
                            />
                            {form.values.billingPlans[i].salesPortalStatus !== 'DISABLED' && (
                              <>
                                <TextInput
                                  size='sm'
                                  disabled={true}
                                  style={{ flexGrow: 1 }}
                                  value={form.values.billingPlans[i].salesPortalUrl}
                                />
                                <Button
                                  size='sm'
                                  onClick={() => {
                                    if (!!form.values.billingPlans[i].salesPortalUrl) {
                                      navigator.clipboard.writeText(
                                        form.values.billingPlans[i].salesPortalUrl,
                                      );
                                      successNotification('Copied URL to clipboard');
                                    }
                                  }}
                                >
                                  Copy URL
                                </Button>
                              </>
                            )}
                          </div>
                        )}

                        {isAthleteSalesPortalEnabled && (
                          <Select
                            disabled={!permission}
                            label='Athlete App Visibility'
                            data={['DISABLED', 'ENABLED']}
                            {...form.getInputProps(`billingPlans.${i}.athleteSalesPortalStatus`)}
                            key={`athleteSalesPortalStatus${i}`}
                            size='sm'
                            sx={{ minWidth: '30%', maxWidth: '30%' }}
                          />
                        )}
                        {((isPackageSalesPortalEnabled &&
                          form.values.billingPlans[i].salesPortalStatus !== 'DISABLED') ||
                          (isAthleteSalesPortalEnabled &&
                            form.values.billingPlans[i].athleteSalesPortalStatus === 'ENABLED')) && (
                          <Group>
                            <Select
                              label='Requires Payment Method'
                              size='sm'
                              disabled={!permission}
                              data={['Yes', 'No']}
                              {...form.getInputProps(`billingPlans.${i}.salesPortalRequiresPaymentMethod`)}
                              key={`salesPortalRequiresPaymentMethod${i}`}
                            />
                          </Group>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              </ExpandableSection>
              {isPackageSalesPortalEnabled && (
                <>
                  <Divider mx='-xl' />
                  <ExpandableSection
                    title='Checkout Flow'
                    opened={formsOpened}
                    handleToggle={handleFormsToggle}
                  >
                    <Text size='md' color='dimmed' pl='xl'>
                      Use this section to customize what information is captured during the checkout flow.
                    </Text>
                    <Group pt='xl'>
                      <FormFieldContent
                        fields={[
                          {
                            'First Name': {
                              isVisible: true,
                              isRequired: true,
                              visibleField: '',
                              requiredField: '',
                              disabled: true,
                            },
                          },
                          {
                            'Last Name': {
                              isVisible: true,
                              isRequired: true,
                              visibleField: '',
                              requiredField: '',
                              disabled: true,
                            },
                          },
                          {
                            Email: {
                              isVisible: true,
                              isRequired: true,
                              visibleField: '',
                              requiredField: '',
                              disabled: true,
                            },
                          },
                          {
                            Phone: {
                              isVisible: true,
                              isRequired: true,
                              visibleField: '',
                              requiredField: '',
                              disabled: true,
                            },
                          },
                          {
                            'Date of Birth': {
                              isVisible: form.values.dobVisible,
                              isRequired: form.values.dobRequired,
                              visibleField: 'dobVisible',
                              requiredField: 'dobRequired',
                              disabled: !permission,
                            },
                          },
                          {
                            Gender: {
                              isVisible: form.values.genderVisible,
                              isRequired: form.values.genderRequired,
                              visibleField: 'genderVisible',
                              requiredField: 'genderRequired',
                              disabled: !permission,
                            },
                            Address: {
                              isVisible: form.values.addressVisible,
                              isRequired: form.values.addressRequired,
                              visibleField: 'addressVisible',
                              requiredField: 'addressRequired',
                              disabled: !permission,
                            },
                          },
                          {
                            'Emergency Contact': {
                              isVisible: form.values.emergencyNameVisible,
                              isRequired: form.values.emergencyNameRequired,
                              visibleField: 'emergencyNameVisible',
                              requiredField: 'emergencyNameRequired',
                              disabled: !permission,
                            },
                          },
                          {
                            'Emergency Phone': {
                              isVisible: form.values.emergencyPhoneVisible,
                              isRequired: form.values.emergencyPhoneRequired,
                              visibleField: 'emergencyPhoneVisible',
                              requiredField: 'emergencyPhoneRequired',
                              disabled: !permission,
                            },
                          },
                          {
                            'Medical Notes': {
                              isVisible: form.values.notesVisible,
                              isRequired: form.values.notesRequired,
                              visibleField: 'notesVisible',
                              requiredField: 'notesRequired',
                              disabled: !permission,
                            },
                          },
                        ]}
                        form={form}
                      />
                    </Group>
                  </ExpandableSection>
                </>
              )}
            </>
          )}
        </Stack>
      </form>
    </Drawer>
  );
};
export default SalesPortalOptionsDrawer;
